import { ErrorCategory, ErrorSeverity, AppError, isAppError } from '@shared/error';

export interface RetryConfig {
  maxAttempts: number;
  delayMs: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: AppError) => boolean;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  shouldRetry: (error) => error.retryable && error.category === ErrorCategory.NETWORK,
};

export function createAppError(
  error: unknown,
  category: ErrorCategory,
  severity: ErrorSeverity,
  userMessage?: string,
  context?: Record<string, unknown>
): AppError {
  const originalError = error instanceof Error ? error : undefined;
  const message = originalError?.message || String(error);
  return {
    category,
    severity,
    message,
    userMessage: userMessage || getDefaultUserMessage(category, message),
    originalError,
    context,
    timestamp: Date.now(),
    retryable: isRetryable(category, message),
  };
}

function isRetryable(category: ErrorCategory, message: string): boolean {
  if (category === ErrorCategory.NETWORK) return true;
  if (category === ErrorCategory.USER_CANCELLED) return false;
  if (category === ErrorCategory.VALIDATION) return false;
  const retryable = [/timeout/i, /ECONNREFUSED/i, /ETIMEDOUT/i, /ENOTFOUND/i, /socket hang up/i, /rate limit/i, /429/];
  return retryable.some((p) => p.test(message));
}

function getDefaultUserMessage(category: ErrorCategory, technicalMessage: string): string {
  switch (category) {
    case ErrorCategory.NETWORK:
      return 'Network connection issue. Please check your internet connection and try again.';
    case ErrorCategory.AUTHENTICATION:
      return 'Authentication failed. Please check your credentials.';
    case ErrorCategory.VALIDATION:
      return 'Invalid input. Please check your values and try again.';
    case ErrorCategory.BUSINESS_LOGIC:
      if (technicalMessage.toLowerCase().includes('insufficient')) return 'Insufficient funds for this operation.';
      return 'This operation could not be completed. Please check the details and try again.';
    case ErrorCategory.USER_CANCELLED:
      return 'Operation cancelled.';
    case ErrorCategory.SYSTEM:
    default:
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  }
}

export async function withRetry<T>(fn: () => Promise<T>, config: Partial<RetryConfig> = {}): Promise<T> {
  const final = { ...DEFAULT_RETRY_CONFIG, ...config };
  let last: AppError | null = null;
  for (let attempt = 1; attempt <= final.maxAttempts; attempt++) {
    try { return await fn(); }
    catch (error) {
      const appError = isAppError(error) ? error : createAppError(error, ErrorCategory.SYSTEM, ErrorSeverity.MEDIUM);
      last = appError;
      const shouldRetry = final.shouldRetry?.(appError) ?? appError.retryable;
      if (!shouldRetry || attempt >= final.maxAttempts) throw appError;
      const delay = final.delayMs * Math.pow(final.backoffMultiplier || 1, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw last;
}

export class ErrorLogger {
  private logs: AppError[] = [];
  private maxLogs = 100;
  log(error: AppError): void {
    this.logs.push(error);
    if (this.logs.length > this.maxLogs) this.logs = this.logs.slice(-this.maxLogs);
    const method = this.getLogMethod(error.severity);
    method('[ErrorLogger]', { category: error.category, severity: error.severity, message: error.message, userMessage: error.userMessage, context: error.context, timestamp: new Date(error.timestamp).toISOString() });
    if (typeof window !== 'undefined' && window.pm && typeof window.pm.log === 'function') {
      try { window.pm.log({ type: 'error', category: error.category, severity: error.severity, message: error.message, userMessage: error.userMessage, context: error.context, timestamp: error.timestamp }); } catch {}
    }
  }
  private getLogMethod(severity: ErrorSeverity): typeof console.log {
    switch (severity) {
      case 'LOW': return console.debug;
      case 'MEDIUM': return console.warn;
      case 'HIGH':
      case 'CRITICAL': return console.error;
      default: return console.log;
    }
  }
  getRecentErrors(count = 10): AppError[] { return this.logs.slice(-count); }
  clearLogs(): void { this.logs = []; }
}

export const errorLogger = new ErrorLogger();

export async function handleError(
  error: unknown,
  category: ErrorCategory,
  severity: ErrorSeverity,
  userMessage?: string,
  context?: Record<string, unknown>,
  notifyUser = true
): Promise<AppError> {
  const appError = isAppError(error) ? error : createAppError(error, category, severity, userMessage, context);
  errorLogger.log(appError);
  if (notifyUser && severity >= ErrorSeverity.MEDIUM) notifyUserOfError(appError);
  return appError;
}

function notifyUserOfError(error: AppError): void {
  if (typeof window !== 'undefined' && (window as any).notifications) {
    try { (window as any).notifications.showAppError(error); } catch {}
  } else {
    if (error.severity === ErrorSeverity.CRITICAL) try { alert(error.userMessage); } catch {}
  }
}

export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  category: ErrorCategory,
  severity: ErrorSeverity,
  userMessage?: string
): T {
  return (async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try { return await fn(...args); }
    catch (error) {
      const appError = await handleError(error, category, severity, userMessage, { functionName: fn.name, args });
      throw appError;
    }
  }) as T;
}
